import React, { useState } from 'react'
import AdditionalInfo from '../common/AdditionalInfo'
import { format } from 'date-fns'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import Popup from '../common/popup/Popup'
import InteractionButtons from '../common/InteractionButtons'
import ReportButton from '../common/ReportButton'
import { ContentType } from '../../utils/enums'
import '../../styles/components/guide/GuideContentDisplay.css'

export function parseAllowedHtml(htmlString) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')
  const allowedTags = ['b', 'i', 'a', 'span', 'div', 'br', 'p']
  const traverse = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase()
      const children = Array.from(node.childNodes).map(traverse)
      if (allowedTags.includes(tagName)) {
        if (tagName === 'a') {
          const href = node.getAttribute('href') || ''
          const safeHref = /^javascript:/i.test(href) ? '#' : href
          return React.createElement('a', { key: Math.random(), href: safeHref, target: '_blank', rel: 'noopener noreferrer' }, ...children)
        }
        return React.createElement(tagName, { key: Math.random() }, ...children)
      } else {
        return React.createElement(React.Fragment, { key: Math.random() }, ...children)
      }
    }
    return null
  }
  return Array.from(doc.body.childNodes).map(traverse)
}

export const InteractionRow = ({ left, right }) => {
  return (
    <div className="interaction-row">
      <div className="interaction-left">{left}</div>
      <div className="interaction-right">{right}</div>
    </div>
  )
}

export const GuideContentHeader = ({ guide, onImageClick, userInteraction, interactionLoading, handleLike, handleDislike }) => {
  const computedAdditionalInfo = guide.additionalInfoData || {
    destinationName: guide.origin_name || 'Unknown Destination',
    destinationLink: guide.origin_id ? `/destination/${guide.origin_id}` : '',
    authorName: guide.author_name || 'Unknown Author',
    updatedDate: guide.updated_datetime_utc ? format(new Date(guide.updated_datetime_utc), 'do MMMM yyyy') : '',
    itineraryLink: guide.itinerary_id ? `/itinerary/${guide.origin_id}/${guide.itinerary_id}` : null,
    tags: guide.tags ? guide.tags.map(tag => tag.name) : []
  }
  return (
    <header className="guide-header">
      <h1 className="guide-title">{guide.title}</h1>
      <AdditionalInfo data={computedAdditionalInfo} />
      <InteractionRow
        left={
          <div className="interaction-buttons-wrapper">
            <InteractionButtons
              userInteraction={userInteraction}
              handleLike={handleLike}
              handleDislike={handleDislike}
              interactionLoading={interactionLoading}
              likeCount={guide.like_count}
              dislikeCount={guide.dislike_count}
              viewCount={guide.view_count}
            />
          </div>
        }
        right={<ReportButton contentId={guide.guide_id} contentType={ContentType.GUIDE} />}
      />
      <div className="affiliate-disclosure">This guide may contain affiliate links. If you click these links and make a purchase, we may earn a commission at no additional cost to you.</div>
      {guide.introduction_image_url && (
        guide.introduction_image_width && guide.introduction_image_height ? (
          <div className="guide-introduction-image-container limited-image-container" style={{ aspectRatio: guide.introduction_image_width / guide.introduction_image_height }}>
            <img
              src={guide.introduction_image_url}
              srcSet={guide.introduction_thumbnail_url ? `${guide.introduction_thumbnail_url} 480w, ${guide.introduction_image_url} 1080w` : undefined}
              sizes="(max-width: 1000px) 100vw, 50vw"
              alt="Introduction"
              className="guide-introduction-image limited-image"
              loading="lazy"
              decoding="async"
              onClick={() => onImageClick && onImageClick(guide.introduction_image_url, guide.introduction_image_description, guide.title, guide.introduction_image_width, guide.introduction_image_height)}
            />
            {guide.introduction_image_description && (
              <div className="image-description">{guide.introduction_image_description}</div>
            )}
          </div>
        ) : (
          <div className="guide-introduction-image-container limited-image-container">
            <img
              src={guide.introduction_image_url}
              alt="Introduction"
              className="guide-introduction-image limited-image"
              loading="lazy"
              decoding="async"
              onClick={() => onImageClick && onImageClick(guide.introduction_image_url, guide.introduction_image_description, guide.title)}
            />
            {guide.introduction_image_description && (
              <div className="image-description">{guide.introduction_image_description}</div>
            )}
          </div>
        )
      )}
    </header>
  )
}

export const GuideContentSections = ({ guide, isAuthor, onEdit, onImageClick }) => {
  return (
    <section className="guide-sections">
      {guide.days.map((day, index) => (
        <article key={index} id={`day-${day.day_number}`} className="guide-day">
          <h2 className="guide-day-heading">{day.subtitle}</h2>
          <div className="guide-day-content">{parseAllowedHtml(day.content)}</div>
          {day.image_url && (
            day.image_width && day.image_height ? (
              <div className="guide-day-image-container limited-image-container" style={{ aspectRatio: day.image_width / day.image_height }}>
                <img
                  src={day.image_url}
                  alt={`Section ${index + 1}`}
                  className="guide-day-image limited-image"
                  loading="lazy"
                  decoding="async"
                  onClick={() => onImageClick && onImageClick(day.image_url, day.image_description, day.subtitle, day.image_width, day.image_height)}
                />
              </div>
            ) : (
              <div className="guide-day-image-container limited-image-container">
                <img
                  src={day.image_url}
                  alt={`Section ${index + 1}`}
                  className="guide-day-image limited-image"
                  loading="lazy"
                  decoding="async"
                  onClick={() => onImageClick && onImageClick(day.image_url, day.image_description, day.subtitle)}
                />
              </div>
            )
          )}
          {day.image_description && (
            <div className="image-description">{day.image_description}</div>
          )}
        </article>
      ))}
    </section>
  )
}

const GuideContentDisplay = (props) => {
  const [popupImage, setPopupImage] = useState(null)
  const [showYoutube, setShowYoutube] = useState(false)
  const handleImageClick = (url, description, popupTitle, width, height) => {
    setPopupImage({ url, description, popupTitle, width, height })
  }
  const getYoutubeVideoId = (url) => {
    const regExp = /(?:youtube\.com\/.*(?:\?|&)v=|youtu\.be\/)([\w-]{11})/
    const match = url.match(regExp)
    return match ? match[1] : null
  }
  return (
    <>
      <GuideContentHeader {...props} onImageClick={handleImageClick} />
      {props.guide.introduction && (
        <div id="introduction" className="guide-introduction">
          {parseAllowedHtml(props.guide.introduction)}
        </div>
      )}
      {props.guide.youtube_url && (
        <>
          <div className="youtube-toggle-container">
            <button className="youtube-toggle-button" onClick={() => setShowYoutube(!showYoutube)} aria-expanded={showYoutube} aria-controls="youtube-video-container">
              {showYoutube ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
              {showYoutube ? "Hide Video" : "Show Video"}
            </button>
          </div>
          <div id="youtube-video-container" className={`youtube-responsive-container ${showYoutube ? "open" : "closed"}`}>
            {showYoutube && (
              <iframe src={`https://www.youtube.com/embed/${getYoutubeVideoId(props.guide.youtube_url)}`} title={`YouTube video for ${props.guide.title}`} allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy"></iframe>
            )}
          </div>
        </>
      )}
      <GuideContentSections {...props} onImageClick={handleImageClick} />
      {popupImage && (
        <Popup title={popupImage.popupTitle} className="popup-image" onCancel={() => setPopupImage(null)}>
          <img
            src={popupImage.url}
            alt={popupImage.description || "Popup Image"}
            className="popup-image-content"
            loading="lazy"
            decoding="async"
          />
        </Popup>
      )}
    </>
  )
}

export default GuideContentDisplay
